import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useScenario } from "@/contexts/ScenarioContext";
import {
  Cpu, MemoryStick, HardDrive, Gauge, Activity, Wifi, Users,
  ArrowDown, ArrowUp, Server, Sparkles,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const tooltipStyle = {
  contentStyle: { background: "#020617", border: "1px solid #1E293B", borderRadius: 2, fontFamily: "JetBrains Mono, IBM Plex Mono", fontSize: 11 },
  labelStyle: { color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 10 },
};

const STATUS = {
  active: { color: "#10B981", label: "ACTIVE" },
  down:   { color: "#EF4444", label: "DOWN" },
};

function Bar({ icon: Icon, label, value, suffix = "%", warn = 75, crit = 90 }) {
  const color = value >= crit ? "#EF4444" : value >= warn ? "#F59E0B" : "#10B981";
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-1.5">
        <span className="flex items-center gap-1.5"><Icon className="w-3 h-3" /> {label}</span>
        <span className="font-bold" style={{ color }}>{value}{suffix}</span>
      </div>
      <div className="h-1.5 bg-slate-800 overflow-hidden">
        <div className="h-full transition-all duration-700" style={{ width: `${Math.min(100, value)}%`, background: color }} />
      </div>
    </div>
  );
}

function fmtCompact(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return String(n);
}

function UnitCard({ u, locale }) {
  const s = STATUS[u.primary_status] || STATUS.active;

  // Identify the most anomalous metric to pre-fill in the Copilot
  const askCopilot = () => {
    const findings = [];
    if (u.cpu >= 80) findings.push(`CPU em ${u.cpu}%`);
    if (u.ram >= 80) findings.push(`RAM em ${u.ram}%`);
    if (u.disk >= 80) findings.push(`disco em ${u.disk}%`);
    if (u.primary_status === "down") findings.push(`link primário (Claro) caído`);
    const concern = findings.length
      ? findings.join(", ")
      : `CPU ${u.cpu}%, RAM ${u.ram}%, disco ${u.disk}%`;
    const prompt = locale === "pt"
      ? `Analisar carga em ${u.role} (${u.short}) — ${concern}. Recomendar próximos passos e qual playbook acionar.`
      : `Analyze load on ${u.role} (${u.short}) — ${concern}. Recommend next steps and which playbook to trigger.`;
    window.dispatchEvent(new CustomEvent("evos:open-copilot", { detail: { prompt } }));
  };

  return (
    <div className="border border-slate-800 bg-[#0F172A] p-5 space-y-4" data-testid={`telemetry-card-${u.id}`}>
      <div className="flex items-start justify-between border-b border-slate-800 pb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#1E3A8A]/20 border border-[#1E3A8A]/40 flex items-center justify-center">
            <Server className="w-4 h-4 text-[#60A5FA]" />
          </div>
          <div>
            <div className="font-display font-bold text-base">{u.role} · {u.short}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{u.hostname}</div>
          </div>
        </div>
        <span className="text-[10px] font-mono font-bold px-2 py-0.5 border rounded-sm whitespace-nowrap"
              style={{ color: s.color, borderColor: s.color + "55", background: s.color + "12" }}>
          ● {s.label}
        </span>
      </div>

      <div className="space-y-3">
        <Bar icon={Cpu} label="CPU" value={u.cpu} />
        <Bar icon={MemoryStick} label="RAM" value={u.ram} />
        <Bar icon={HardDrive} label={locale === "pt" ? "Disco" : "Disk"} value={u.disk} />
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Stat icon={Wifi} label="IPsec" value={u.ipsec_tunnels} color="#3B82F6" />
        <Stat icon={Users} label={locale === "pt" ? "Sessões" : "Sessions"} value={fmtCompact(u.active_sessions)} color="#A855F7" />
        <Stat icon={Gauge} label="PPS" value={fmtCompact(u.pps)} color="#F59E0B" />
      </div>

      <div className="border-t border-slate-800 pt-3">
        <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">
          <span>{locale === "pt" ? "Banda · 24h" : "Bandwidth · 24h"}</span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1"><ArrowDown className="w-2.5 h-2.5 text-[#60A5FA]" /><span className="text-[#60A5FA]">DL</span></span>
            <span className="flex items-center gap-1"><ArrowUp className="w-2.5 h-2.5 text-[#34D399]" /><span className="text-[#34D399]">UL</span></span>
          </span>
        </div>
        <ResponsiveContainer width="100%" height={110}>
          <AreaChart data={u.bandwidth_24h} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
            <defs>
              <linearGradient id={`dl-${u.id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id={`ul-${u.id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10B981" stopOpacity={0.32} />
                <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="t" stroke="#475569" fontSize={8} tick={false} />
            <YAxis stroke="#475569" fontSize={8} />
            <Tooltip {...tooltipStyle} />
            <Area type="monotone" dataKey="download" stroke="#3B82F6" strokeWidth={1.5} fill={`url(#dl-${u.id})`} />
            <Area type="monotone" dataKey="upload" stroke="#10B981" strokeWidth={1.5} fill={`url(#ul-${u.id})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <button
        data-testid={`telemetry-copilot-${u.id}`}
        onClick={askCopilot}
        className="w-full bg-[#172554] hover:bg-[#1E3A8A] transition-colors border border-[#1E3A8A]/60 text-slate-100 px-3 py-2 rounded-sm text-xs font-semibold flex items-center justify-center gap-2 group"
      >
        <Sparkles className="w-3.5 h-3.5 text-[#60A5FA] group-hover:scale-110 transition-transform" />
        {locale === "pt" ? "Analisar carga com Copilot" : "Analyze load with Copilot"}
      </button>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="border border-slate-800 bg-[#020617] p-2.5">
      <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-0.5">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-mono font-bold text-sm" style={{ color, fontFamily: "JetBrains Mono, IBM Plex Mono" }}>{value}</div>
    </div>
  );
}

export default function Telemetry() {
  const { t, locale } = useI18n();
  const { scenario } = useScenario();
  const [units, setUnits] = useState([]);

  useEffect(() => {
    if (!scenario) return;
    const load = () => api.get(`/telemetry/units?scenario=${scenario.id}`).then(({ data }) => setUnits(data));
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [scenario]);

  // Aggregate footer
  const agg = units.length ? {
    cpu: Math.round(units.reduce((a, u) => a + u.cpu, 0) / units.length),
    ram: Math.round(units.reduce((a, u) => a + u.ram, 0) / units.length),
    tunnels: units.reduce((a, u) => a + u.ipsec_tunnels, 0),
    sessions: units.reduce((a, u) => a + u.active_sessions, 0),
    pps: units.reduce((a, u) => a + u.pps, 0),
  } : null;

  return (
    <div className="space-y-6" data-testid="telemetry-page" style={{ fontFamily: "JetBrains Mono, IBM Plex Mono, monospace" }}>
      <div className="border-b border-slate-800 pb-5 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2" style={{ fontFamily: "JetBrains Mono, IBM Plex Mono" }}>
            {locale === "pt" ? "Telemetria Profunda · Tempo Real" : "Deep Telemetry · Real-time"}
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo, sans-serif" }}>
            {locale === "pt" ? "Observabilidade & Telemetria" : "Observability & Telemetry"}
          </h1>
          <p className="text-sm text-slate-400 mt-1" style={{ fontFamily: "IBM Plex Sans, sans-serif" }}>
            {locale === "pt"
              ? "Hardware, banda passante e conexões ativas por gateway · atualiza a cada 4s"
              : "Hardware, bandwidth and active connections per gateway · refreshes every 4s"}
          </p>
        </div>
        {agg && (
          <div className="hidden md:flex items-center gap-3 text-[10px] uppercase tracking-[0.2em]">
            <div className="border border-slate-800 px-3 py-1.5 bg-[#0F172A]">
              <span className="text-slate-500">FLEET AVG CPU</span>{" "}
              <span className="text-[#60A5FA] font-bold">{agg.cpu}%</span>
            </div>
            <div className="border border-slate-800 px-3 py-1.5 bg-[#0F172A]">
              <span className="text-slate-500">FLEET AVG RAM</span>{" "}
              <span className="text-[#A855F7] font-bold">{agg.ram}%</span>
            </div>
            <div className="border border-slate-800 px-3 py-1.5 bg-[#0F172A]">
              <span className="text-slate-500">{locale === "pt" ? "TÚNEIS" : "TUNNELS"}</span>{" "}
              <span className="text-emerald-400 font-bold">{agg.tunnels}</span>
            </div>
          </div>
        )}
      </div>

      {/* Fleet KPI strip */}
      {agg && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <FleetTile icon={Cpu} label={locale === "pt" ? "CPU média" : "Avg CPU"} value={`${agg.cpu}%`} color="#60A5FA" />
          <FleetTile icon={MemoryStick} label={locale === "pt" ? "RAM média" : "Avg RAM"} value={`${agg.ram}%`} color="#A855F7" />
          <FleetTile icon={Wifi} label={locale === "pt" ? "Túneis IPsec" : "IPsec Tunnels"} value={agg.tunnels} color="#10B981" />
          <FleetTile icon={Users} label={locale === "pt" ? "Sessões" : "Sessions"} value={fmtCompact(agg.sessions)} color="#F59E0B" />
          <FleetTile icon={Activity} label="PPS" value={fmtCompact(agg.pps)} color="#EF4444" />
        </div>
      )}

      {/* Units grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {units.map((u) => <UnitCard key={u.id} u={u} locale={locale} />)}
      </div>
    </div>
  );
}

function FleetTile({ icon: Icon, label, value, color }) {
  return (
    <div className="border border-slate-800 bg-[#0F172A] p-4 flex items-center gap-3">
      <div className="w-9 h-9 flex items-center justify-center border" style={{ borderColor: color + "55", background: color + "12" }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <div className="text-2xl font-bold" style={{ color, fontFamily: "JetBrains Mono, IBM Plex Mono" }}>{value}</div>
      </div>
    </div>
  );
}
